import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Share2, Facebook, Twitter, MessageCircle, Link as LinkIcon, Check } from 'lucide-react';

export default function SocialShare({ title = 'Primavera Cavese', url, compact = false }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const popRef = useRef(null);

  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title);

  const links = [
    { icon: Facebook, label: 'Facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, bg: '#1877F2' },
    { icon: Twitter, label: 'X/Twitter', href: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`, bg: '#000' },
    { icon: MessageCircle, label: 'WhatsApp', href: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`, bg: '#25D366' },
  ];

  // Posiziona la tendina sotto al pulsante (posizione fissa nella viewport, esce da overflow:hidden)
  const updatePos = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const popW = 220; // larghezza stimata della tendina compatta
    const margin = 8;
    let left = r.right - popW; // allinea a destra del pulsante
    if (left < margin) left = margin;
    if (left + popW > window.innerWidth - margin) left = window.innerWidth - popW - margin;
    setPos({ top: r.bottom + 6, left });
  };

  useEffect(() => {
    if (!open) return;
    updatePos();
    const handler = () => updatePos();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    const onClickOutside = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('touchstart', onClickOutside);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('touchstart', onClickOutside);
    };
  }, [open]);

  const copy = async (e) => {
    e?.stopPropagation();
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) { /* no-op */ }
  };

  const toggle = (e) => {
    e?.stopPropagation();
    e?.preventDefault();
    setOpen((o) => !o);
  };

  if (compact) {
    return (
      <>
        <button
          ref={btnRef}
          onClick={toggle}
          className="w-9 h-9 rounded-full bg-pink-brand text-white flex items-center justify-center hover:scale-110 transition-transform shadow-md"
          aria-label="Condividi"
          data-testid="share-compact-trigger"
        >
          <Share2 size={16} />
        </button>
        {open && typeof document !== 'undefined' && createPortal(
          <div
            ref={popRef}
            style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }}
            className="bg-navy-dark border border-white/15 rounded-xl p-2 shadow-2xl flex gap-1 animate-fade-in"
            data-testid="share-compact-menu"
            onClick={(e) => e.stopPropagation()}
          >
            {links.map((l) => {
              const Icon = l.icon;
              return (
                <a
                  key={l.label}
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg flex items-center justify-center text-white hover:scale-110 transition-transform"
                  style={{ backgroundColor: l.bg }}
                  aria-label={l.label}
                  onClick={() => setOpen(false)}
                >
                  <Icon size={15} />
                </a>
              );
            })}
            <button
              onClick={copy}
              className="w-9 h-9 rounded-lg bg-white/10 border border-white/20 text-white flex items-center justify-center hover:bg-white/20"
              aria-label="Copia link"
            >
              {copied ? <Check size={15} /> : <LinkIcon size={15} />}
            </button>
          </div>,
          document.body,
        )}
      </>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      <span className="text-white/60 text-xs font-bold uppercase tracking-widest flex items-center gap-2">
        <Share2 size={14} /> Condividi
      </span>
      {links.map((l) => {
        const Icon = l.icon;
        return (
          <a
            key={l.label}
            href={l.href}
            target="_blank"
            rel="noopener noreferrer"
            className="w-10 h-10 rounded-full text-white flex items-center justify-center hover:scale-110 transition-transform shadow-md"
            style={{ backgroundColor: l.bg }}
            aria-label={l.label}
          >
            <Icon size={16} />
          </a>
        );
      })}
      <button
        onClick={copy}
        className="h-10 px-4 rounded-full bg-white/10 border border-white/20 text-white text-xs font-bold uppercase tracking-widest flex items-center gap-2 hover:bg-white/20"
      >
        {copied ? <><Check size={14} /> Copiato</> : <><LinkIcon size={14} /> Copia link</>}
      </button>
    </div>
  );
}
